import { i18n } from "next-i18next";
import { TransformOfferType } from "../../../services/redux/types/TypeHelper";
import { SuccessReportTypes } from "../../ErrorTypes";
import LS from "../../LocalStorage";
import {
  DISPLAYED_NAME,
  I18LanguagesEnum,
  LocalStorageEnum,
} from "../../enums";
import {
  AddonGroupType,
  CategoryType,
  Franchise,
  ItemType,
  OrderItemList,
  PointingSystem,
  SubItemType,
} from "../../types";

export const isArabicLanguage = () => {
  return [I18LanguagesEnum.AR, I18LanguagesEnum.SA].includes(
    i18n?.language as I18LanguagesEnum
  );
};

export const getLocalizedText = (
  text: string,
  textLocalized: string | undefined
) => {
  return i18n?.language !== I18LanguagesEnum.EN && textLocalized
    ? textLocalized
    : text;
};

// A branch is identified to the user by its name; the (localized) address is
// only a fallback for franchises that have no name configured.
export const getFranchiseDisplayName = (
  franchise?: Pick<Franchise, "name" | "address" | "addressLocalized">
): string | undefined => {
  if (!franchise) {
    return undefined;
  }

  return (
    franchise.name ||
    getLocalizedText(franchise.address, franchise.addressLocalized)
  );
};

export const localizedNumber = (number: string | number | undefined) => {
  if (number === undefined) {
    return "";
  }
  const numberStr = number.toString();

  if (isArabicLanguage()) {
    return numberStr
      .replace(/\d/g, (d: any) => "٠١٢٣٤٥٦٧٨٩"[d])
      .replaceAll(".", "٫");
  }

  return numberStr;
};

export const localizedSingularPluralKey = (
  number: number,
  singularKey: string,
  pluralKey: string
) => {
  if (isArabicLanguage()) {
    return number > 10 ? singularKey : pluralKey;
  }

  return number === 1 ? singularKey : pluralKey;
};

export const returnSingularOrPlural = (unit?: string, quantity?: number) => {
  const isEnglish = LS._get(LocalStorageEnum.LANG) === "en";
  if (!isEnglish) {
    return unit;
  }

  return !unit ? unit : unit + (!quantity || quantity !== 1 ? "s" : "");
};

export const numberToLetters = (
  inpNumber: number,
  t: (key: string) => string
) => {
  const numbers = [
    { number: 1, value: t("addons:number-one") },
    { number: 2, value: t("addons:number-two") },
    { number: 3, value: t("addons:number-three") },
    { number: 4, value: t("addons:number-four") },
    { number: 5, value: t("addons:number-five") },
    { number: 6, value: t("addons:number-six") },
    { number: 7, value: t("addons:number-seven") },
    { number: 8, value: t("addons:number-eight") },
    { number: 9, value: t("addons:number-nine") },
    { number: 10, value: t("addons:number-ten") },
    { number: 11, value: t("addons:number-eleven") },
    { number: 12, value: t("addons:number-twelve") },
  ];
  const res = numbers.findIndex((num) => num.number === inpNumber);
  if (res > -1) {
    return numbers[res];
  }

  return { number: inpNumber, value: inpNumber.toString() };
};

export const getErrorInfoKeyFromError = (error: SuccessReportTypes) => {
  switch (error) {
    case SuccessReportTypes.LoyaltyPackageService_GetValidOffer_OfferNotFound:
      return "offers:offer-error-new-customers-only";

    case SuccessReportTypes.ReceiptService_createUpdateReceiptWithReceiptItems_MinSpendNotAchieved:
      return "offers:offer-error-min-spend";

    default:
      return "offers:offer-error-generic";
  }
};

export const distanceText = (
  distanceInMeters: number | undefined,
  t: (key: string) => string
): string => {
  const kilometerAbv = t("table-page:kilometer-abv");
  const meterAbv = t("table-page:meter-abv");

  if (distanceInMeters === undefined) {
    return "";
  }

  if (distanceInMeters >= 1000) {
    // Convert meters to kilometers with one decimal place
    return `${localizedNumber(
      (distanceInMeters / 1000).toFixed(1)
    )} ${kilometerAbv}`;
  }

  // If less than 1000 meters, just display the meters
  return `${localizedNumber(distanceInMeters)} ${meterAbv}`;
};

export const getPointingSystemName = (
  pointingSystem?: TransformOfferType<PointingSystem>,
  defaultName?: string
) => {
  if (!pointingSystem?.pointingSystemName) {
    return defaultName ?? "point";
  }
  const isEnglish = LS._get(LocalStorageEnum.LANG) === "en";
  if (isEnglish || !pointingSystem.pointingSystemNameLocalized) {
    return pointingSystem.pointingSystemName;
  }

  return pointingSystem.pointingSystemNameLocalized;
};

export const globalTranslationFiles = [
  "footer",
  "common",
  "terms",
  "sidemenu",
  "delivery",
  "offers",
  "receipt",
];

export const getDisplayedName = (obj: any, displayedName: DISPLAYED_NAME) => {
  const isEnglish = LS._get(LocalStorageEnum.LANG) === "en";

  let name;
  switch (displayedName) {
    case DISPLAYED_NAME.CATEGORY_NAME:
      const category = obj as CategoryType;
      name =
        !isEnglish && category?.nameLocalized
          ? category.nameLocalized
          : category.category_name;
      break;
    case DISPLAYED_NAME.TITLE:
      const title = obj as AddonGroupType;
      name =
        !isEnglish && title?.nameLocalized
          ? title?.nameLocalized
          : title?.displayTitle;
      break;
    case DISPLAYED_NAME.NAME:
      const product = obj as AddonGroupType | SubItemType | OrderItemList;
      name =
        !isEnglish && product?.nameLocalized
          ? product?.nameLocalized
          : product?.name;
      break;
    case DISPLAYED_NAME.FULL_NAME:
      const subItem = obj as SubItemType;
      name =
        !isEnglish && subItem?.fullNameLocalized
          ? subItem.fullNameLocalized
          : subItem.fullName
          ? subItem.fullName
          : null;
      break;
    case DISPLAYED_NAME.ITEM_NAME:
      const item = obj as ItemType;
      name =
        !isEnglish && item?.nameLocalized ? item.nameLocalized : item.item_name;
      break;
    case DISPLAYED_NAME.DESCRIPTION:
      const itemDes = obj as ItemType;
      name =
        !isEnglish && itemDes?.descriptionLocalized
          ? itemDes.descriptionLocalized
          : itemDes.description;
      break;

    default:
      break;
  }

  return name?.replace(/PRPT/g, "");
};
